/**
 * BulkActionBar — sticky bottom bar with bulk activate/deactivate/reassign.
 *
 * Extracted from BrandAssetGallery.jsx during May 2026 refactor.
 */
import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { API, ORIGINS, POOLS, authedFetch } from "./utils";

function BulkActionBar({ selectedIds, totalMatching, filters, onClear, onApplied }) {
  const [scope, setScope] = useState("selected"); // selected | all_filtered
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState("");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const count = scope === "selected" ? selectedIds.length : totalMatching;
  if (selectedIds.length === 0) return null;

  const openWith = (a) => {
    setAction(a);
    setValue("");
    setReason("");
    setOpen(true);
  };

  const submit = async () => {
    if (!action) return;
    setSubmitting(true);
    const ws = filters._ws;
    const updates = {};
    if (action === "activate") updates.is_active = true;
    if (action === "deactivate") updates.is_active = false;
    if (action === "set_origin") updates.origin = value;
    if (action === "set_pool") updates.pool = value;

    let body;
    if (scope === "selected") {
      body = { asset_ids: selectedIds, updates, reason: reason || null };
    } else {
      const f = {};
      if (filters.origin) f.origin = filters.origin.split(",");
      if (filters.pool) f.pool = filters.pool.split(",");
      if (filters.is_active && filters.is_active !== "all")
        f.is_active = filters.is_active === "true";
      if (filters.source_folder) f.source_folder = filters.source_folder;
      if (filters.q) f.q = filters.q;
      body = { filter: f, updates, reason: reason || null };
    }

    try {
      const r = await authedFetch(
        `${API}/v2/workspaces/${ws}/brand/assets/bulk-update`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success(
        `Updated ${data.updated_count} of ${data.matched_count ?? count} asset(s)`
      );
      setOpen(false);
      onApplied();
    } catch (e) {
      toast.error(`Bulk update failed: ${e.message}`);
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 bg-slate-900 text-white rounded-full shadow-xl px-4 py-2 flex items-center gap-3 max-w-[95vw]"
        data-testid="gallery-bulk-bar"
      >
        <span className="text-sm font-medium" data-testid="gallery-bulk-count">
          {selectedIds.length} selected
        </span>
        <div className="flex items-center gap-2 text-xs">
          <span className="opacity-70">Apply to:</span>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger
              className="h-7 bg-slate-800 border-slate-700 text-white text-xs"
              data-testid="gallery-bulk-scope"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="selected">Selected ({selectedIds.length})</SelectItem>
              <SelectItem value="all_filtered">All matching ({totalMatching})</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => openWith("activate")}
          data-testid="gallery-bulk-activate"
        >
          Activate
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => openWith("deactivate")}
          data-testid="gallery-bulk-deactivate"
        >
          Deactivate
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => openWith("set_pool")}
          data-testid="gallery-bulk-pool"
        >
          Set pool…
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => openWith("set_origin")}
          data-testid="gallery-bulk-origin"
        >
          Flag origin…
        </Button>
        <button
          onClick={onClear}
          className="ml-1 opacity-70 hover:opacity-100"
          data-testid="gallery-bulk-clear"
          aria-label="Clear selection"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="gallery-bulk-dialog">
          <DialogHeader>
            <DialogTitle>
              {action === "activate" && `Activate ${count} asset(s)`}
              {action === "deactivate" && `Deactivate ${count} asset(s)`}
              {action === "set_pool" && `Reassign pool for ${count} asset(s)`}
              {action === "set_origin" && `Flag origin for ${count} asset(s)`}
            </DialogTitle>
          </DialogHeader>
          {(action === "set_pool" || action === "set_origin") && (
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {action === "set_pool" ? "New pool" : "New origin"}
              </label>
              <Select value={value} onValueChange={setValue}>
                <SelectTrigger data-testid="gallery-bulk-value">
                  <SelectValue placeholder="Choose…" />
                </SelectTrigger>
                <SelectContent>
                  {(action === "set_pool" ? POOLS : ORIGINS).map((v) => (
                    <SelectItem key={v} value={v}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <label className="text-sm font-medium">Reason (audit log)</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you making this change?"
              data-testid="gallery-bulk-reason"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="gallery-bulk-cancel">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={
                submitting ||
                ((action === "set_pool" || action === "set_origin") && !value)
              }
              data-testid="gallery-bulk-confirm"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Apply to {count}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Detail modal ───────────────────────────────────────────────────────────
// ─── Phase 1.4 — Classifier verdict panel ─────────────────────────────────
// Renders the auto_classification envelope: top-level verdict + reason,
// a collapsible per-signal table, and a "Why this verdict?" sub-panel
// pulling tags/meta. Read-only — operator override goes through the
// existing PATCH on is_active etc. (operator_review is written server-side

export default BulkActionBar;
export { BulkActionBar };
